<template>
  <JazelAppContainer
    app-name="Autofocus Highlights 2.0"
    @update:account-settings="accountSettings.setAccountSettings($event)"
    @update:session-token="accountSettings.setSessionToken($event)"
    @update:is-client-user="accountSettings.setIsClientUser($event)"
    @update:user-role="accountSettings.setRole($event)"
    @update:product-settings="accountSettings.setProductSettings($event)"
  >
    <!-- Refresh vehicle loader -->
    <transition name="fade">
      <div
        v-if="refreshingVehicles"
        class="d-flex flex-column justify-center"
        :style="{ borderRadius: 20, height: '80vh', margin: 'auto', width: '500px' }"
      >
        <div class="text-red text-center">Do not close this window until completed.</div>
        <v-progress-linear
          :model-value="progress"
          :color="syncProgressColor"
          height="20"
          rounded
        />
        <span class="text-grey-darken-2">
          {{ progress }}% · {{ progressMessage }}
          <template v-if="descriptionSyncJob.job">
            {{ descriptionSyncJob.job.completedVehicles }} of
            {{ descriptionSyncJob.job.totalVehicles }} vehicles
          </template>
        </span>
      </div>
    </transition>

    <div v-if="!refreshingVehicles">
      <!-- Top navigation tabs (Inventory only) with Actions menu on the right -->
      <div class="d-flex align-center justify-space-between mb-4">
        <v-tabs v-model="activeTab" class="d-flex">
          <v-tab value="inventory" data-testid="inventory-tab">
            <v-icon start class="mr-sm-2">mdi-view-dashboard</v-icon>
            <span>Inventory</span>
          </v-tab>
        </v-tabs>
        <div class="d-flex align-center">
          <v-menu location="bottom end" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                color="grey-darken-1"
                class="text-caption text-medium-emphasis"
                aria-label="More actions"
                min-width="60"
              >
                <v-icon size="small" class="mr-1">mdi-dots-vertical</v-icon>
                <span class="d-inline-block">Actions</span>
              </v-btn>
            </template>
            <v-list density="compact" class="pa-2">
              <!-- Auto Generation Section Header -->
              <v-list-subheader v-if="hasAdminAccess" class="px-3 pt-2 pb-1">
                <div class="d-flex align-center justify-space-between w-100">
                  <span class="text-caption font-weight-medium text-uppercase" style="letter-spacing: 0.05em; opacity: 0.7; font-size: 0.8rem;">
                    Auto Generation
                  </span>
                  <v-btn
                    variant="text"
                    size="x-small"
                    color="grey-darken-1"
                    :ripple="false"
                    icon="mdi-information-outline"
                    aria-label="More info about Auto-generate content"
                    @click.stop="showAutoGenInfoDialog = true"
                    class="section-info-btn"
                    style="margin-left: 4px;"
                  />
                </div>
              </v-list-subheader>

              <!-- Highlights Toggle -->
              <v-list-item v-if="hasAdminAccess" class="px-3 py-1">
                <div class="d-flex align-center w-100">
                  <span class="text-body-2 flex-grow-1">Highlights</span>
                  <v-btn-toggle
                    :model-value="autoGenerateHighlights"
                    mandatory
                    color="success"
                    density="compact"
                    @update:modelValue="onToggleAutoGenerateHighlights"
                    class="pill-toggle ml-auto"
                    rounded="pill"
                    style="background: rgba(0, 0, 0, 0.06); padding: 2px;"
                  >
                    <v-btn
                      :value="false"
                      size="x-small"
                      class="text-caption text-none px-3"
                      rounded="pill"
                      style="letter-spacing: normal; min-width: 40px;"
                    >
                      Off
                    </v-btn>
                    <v-btn
                      :value="true"
                      size="x-small"
                      class="text-caption text-none px-3"
                      rounded="pill"
                      style="letter-spacing: normal; min-width: 40px;"
                    >
                      On
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </v-list-item>

              <!-- Descriptions Toggle -->
              <v-list-item v-if="hasAdminAccess" class="px-3 py-1">
                <div class="d-flex align-center w-100">
                  <span class="text-body-2 flex-grow-1">Descriptions</span>
                  <v-btn-toggle
                    :model-value="autoGenerateDescriptions"
                    mandatory
                    color="success"
                    density="compact"
                    @update:modelValue="onToggleAutoGenerateDescriptions"
                    class="pill-toggle ml-auto"
                    rounded="pill"
                    style="background: rgba(0, 0, 0, 0.06); padding: 2px;"
                  >
                    <v-btn
                      :value="false"
                      size="x-small"
                      class="text-caption text-none px-3"
                      rounded="pill"
                      style="letter-spacing: normal; min-width: 40px;"
                    >
                      Off
                    </v-btn>
                    <v-btn
                      :value="true"
                      size="x-small"
                      class="text-caption text-none px-3"
                      rounded="pill"
                      style="letter-spacing: normal; min-width: 40px;"
                    >
                      On
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </v-list-item>

              <!-- Auto-generate Mode Selection (Radio Buttons) -->
              <!-- Only show AI Mode when descriptions are enabled, as it primarily controls description regeneration behavior -->
              <v-list-item v-if="autoGenerateDescriptions && hasAdminAccess" class="px-3 py-1">
                <div class="text-caption text-medium-emphasis mb-1" style="opacity: 0.8">AI Mode (for Descriptions)</div>
                <v-radio-group
                  :model-value="autoGenMode"
                  inline
                  hide-details
                  density="compact"
                  @update:modelValue="(v: any) => onChangeAutoGenMode(v ?? 'fill-missing')"
                >
                  <v-radio value="fill-missing" color="primary">
                    <template #label>
                      <span class="text-caption text-medium-emphasis">AI When Missing</span>
                    </template>
                  </v-radio>
                  <v-radio value="always-ai" color="primary" class="ml-2">
                    <template #label>
                      <span class="text-caption text-medium-emphasis">Full AI Mode</span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-list-item>

              <template v-if="hasContentLanguageCapability">
                <v-divider class="my-2"></v-divider>

                <ContentLanguageSettings
                  @saved="onSettingSaved"
                  @error="onSettingError"
                />

                <!-- Applies the languages above to existing inventory. -->
                <ApplyContentLanguage />

                <v-divider class="my-2"></v-divider>
              </template>

              <!-- Actions Section Header -->
              <v-list-subheader class="text-caption font-weight-medium text-uppercase px-3 pt-4 pb-1" style="letter-spacing: 0.05em; opacity: 0.7; font-size: 0.8rem;">
                Actions
              </v-list-subheader>

              <!-- Copy Embed Code -->
              <v-list-item @click="copyToClipboard" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-content-copy</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">
                  {{ copyStatus ? copyStatus : 'Copy Embed Code' }}
                </v-list-item-title>
              </v-list-item>

              <!-- Sync Descriptions -->
              <v-list-item v-if="hasOwnerAccess" @click="showRefreshVehiclesDialog = true" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-sync</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">Sync Descriptions</v-list-item-title>
              </v-list-item>

              <!-- Generate Content (Owner Only) -->
              <v-list-item v-if="hasOwnerAccess" @click="showGenerateContentDialog = true" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-star-circle-outline</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">Generate Content</v-list-item-title>
              </v-list-item>

              <v-divider class="my-2"></v-divider>

              <!-- Management Section Header -->
              <v-list-subheader class="text-caption font-weight-medium text-uppercase px-3 pt-4 pb-1" style="letter-spacing: 0.05em; opacity: 0.7; font-size: 0.8rem;">
                Management
              </v-list-subheader>

              <!-- Manage Prompts (Admin Only) -->
              <v-list-item v-if="hasAdminAccess" @click="navigateToPromptManager" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-file-document-edit</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">Manage Prompts</v-list-item-title>
              </v-list-item>

              <!-- Edit Account Instructions (Owner/Admin Restriction: company_owner or client_admin) -->
              <v-list-item v-if="currentRole === 'company_owner' || currentRole === 'company_suadmin'" @click="navigateToAccountInstructions" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-tune</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">Manage Account Instructions</v-list-item-title>
              </v-list-item>

              <!-- Edit System Instructions (Owner Only) -->
              <v-list-item v-if="hasOwnerAccess" @click="navigateToSystemInstructions" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-cog-outline</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">Manage System Instructions</v-list-item-title>
              </v-list-item>

              <!-- Manage Guardrails (Owner Only) -->
              <v-list-item v-if="hasOwnerAccess" @click="navigateToGuardrailManager" density="compact">
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-shield-lock</v-icon>
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">Manage Guardrails</v-list-item-title>
              </v-list-item>

            </v-list>
          </v-menu>
        </div>
      </div>

      <!-- Above the views on purpose: the job outlives any one of them. -->
      <LanguageJobProgress />

      <v-row>
        <!-- Auto-generate info dialog -->
        <v-dialog v-model="showAutoGenInfoDialog" max-width="520">
          <v-card rounded="lg">
            <v-card-title class="text-subtitle-1 d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-auto-fix</v-icon>
              Auto‑Generate Content
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
              <div class="text-body-2 text-medium-emphasis mb-4">
                Automatically creates highlights and descriptions for your inventory every 2 hours.
              </div>
              <v-alert type="info" variant="tonal" density="comfortable" class="mb-3">
                <div class="text-body-2"><strong>AI Mode (for Descriptions)</strong></div>
                <div class="text-caption text-medium-emphasis">Controls how description content is generated.</div>
              </v-alert>
              <div class="mb-2">
                <div class="text-body-2"><strong>AI When Missing</strong></div>
                <div class="text-caption text-medium-emphasis">Only adds content when a feed description is missing.</div>
                <div class="text-caption text-medium-emphasis mt-1">New feed descriptions are applied automatically unless the description is locked.</div>
              </div>
              <div>
                <div class="text-body-2"><strong>Full AI Mode</strong></div>
                <div class="text-caption text-medium-emphasis">Uses AI‑generated content for all vehicles unless the description is locked.</div>
              </div>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-spacer />
              <v-btn variant="text" color="primary" @click="showAutoGenInfoDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- Vehicle List View -->
        <v-col cols="12" v-if="currentView === 'list' && vehicleStore.selectedVehicle === null">
          <v-card flat class="pt-2">
            <v-card-title
              class="d-flex flex-column-reverse flex-sm-row justify-center justify-sm-space-between align-center pe-2 mb-3"
            >
              <v-row class="w-100">
                <!-- Status filters on the left of the table header -->
                <v-col cols="12" sm="9" class="d-flex flex-column flex-lg-row align-start align-lg-center flex-wrap pb-0">
                  <!-- Description Filters -->
                  <div :class="isMobile ? 'd-flex flex-column align-start w-100 mb-1' : 'd-flex align-center mr-4'" v-if="descriptionFilters.length > 0">
                    <span class="text-caption text-uppercase text-medium-emphasis mr-2" style="letter-spacing: .05em;">DESCRIPTIONS</span>
                    <v-chip-group
                      v-model="selectedDescriptionFilters"
                      :column="isMobile"
                      multiple
                      class="w-100"
                    >
                      <v-chip
                        v-for="filter in descriptionFilters"
                        :key="filter.value"
                        :value="filter.value"
                        :variant="selectedDescriptionFilters.includes(filter.value) ? 'tonal' : 'outlined'"
                        :size="isMobile ? 'small' : 'small'"
                        :color="getStatusColor(filter.label, 'description')"
                        class="mr-1 mb-2 text-no-wrap"
                        style="white-space: nowrap;"
                      >
                        {{ filter.label }}
                      </v-chip>
                    </v-chip-group>
                  </div>

                  <v-divider class="d-none d-lg-block ml-2 mr-4" vertical></v-divider>

                  <!-- Highlight Filters -->
                  <div :class="isMobile ? 'd-flex flex-column align-start w-100 mt-1' : 'd-flex align-center'" v-if="highlightFilters.length > 0">
                    <span class="text-caption text-uppercase text-medium-emphasis mr-2" style="letter-spacing: .05em;">HIGHLIGHTS</span>
                    <v-chip-group
                      v-model="selectedHighlightFilters"
                      :column="isMobile"
                      multiple
                      class="w-100"
                    >
                      <v-chip
                        v-for="filter in highlightFilters"
                        :key="filter.value"
                        :value="filter.value"
                        :variant="selectedHighlightFilters.includes(filter.value) ? 'tonal' : 'outlined'"
                        :size="isMobile ? 'small' : 'small'"
                        :color="getStatusColor(filter.label, 'highlight')"
                        class="mr-1 mb-2 text-no-wrap"
                        style="white-space: nowrap;"
                      >
                        {{ filter.label }}
                      </v-chip>
                    </v-chip-group>
                  </div>
                </v-col>

                <!-- Search -->
                <v-col cols="12" md="3" class="d-flex align-center justify-end py-0">
                  <v-text-field
                    v-model="search"
                    density="compact"
                    label="Search"
                    prepend-inner-icon="mdi-magnify"
                    :append-inner-icon="search ? 'mdi-close' : ''"
                    @click:append-inner="search = ''"
                    :variant="isMobile ? 'solo-filled' : 'outlined'"
                    :bg-color="isMobile ? 'white' : undefined"
                    flat
                    hide-details
                    single-line
                    :class="isMobile ? 'ma-0' : 'ma-0 bg-white'"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Baseline Prompt Dialog -->
            <v-dialog v-model="showBaselineDialog" max-width="720px">
              <v-card>
                <v-card-title>Account Instructions</v-card-title>
                <v-card-text>
                  <v-tabs v-model="baselineTab" grow>
                    <v-tab value="description">Description</v-tab>
                    <v-tab value="highlight">Highlight</v-tab>
                  </v-tabs>
                  <v-window v-model="baselineTab">
                    <v-window-item value="description">
                      <v-textarea
                        v-model="baselineDescription"
                        label="Description Instructions"
                        variant="outlined"
                        rows="8"
                        class="mt-3"
                      />
                    </v-window-item>
                    <v-window-item value="highlight">
                      <v-textarea
                        v-model="baselineHighlight"
                        label="Highlight Instructions"
                        variant="outlined"
                        rows="8"
                        class="mt-3"
                      />
                    </v-window-item>
                  </v-window>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text="Cancel" @click="showBaselineDialog = false" />
                  <v-btn color="primary" :loading="isSavingBaseline" text="Save" @click="saveBaselinePrompts" />
                </v-card-actions>
              </v-card>
            </v-dialog>
            <div class="pa-4 pb-2">
              <!-- Filter Summary (always visible) -->
              <v-row class="mb-2">
                <v-col cols="12">
                  <div :class="isMobile ? 'd-flex flex-column align-start' : 'd-flex align-center'">
                    <v-chip
                      variant="tonal"
                      color="primary"
                      :size="isMobile ? 'default' : 'small'"
                      class="mr-2 mb-2"
                    >
                      {{ filterSummaryText }}
                    </v-chip>
                    <v-btn
                      variant="text"
                      :size="isMobile ? 'default' : 'small'"
                      color="grey-darken-1"
                      @click="clearAllFilters"
                      prepend-icon="mdi-close"
                      class="mb-2"
                      v-if="selectedDescriptionFilters.length > 0 || selectedHighlightFilters.length > 0"
                    >
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-data-table
              :loading="isLoadingListing"
              density="comfortable"
              :headers="tableHeaders"
              :items="filteredVehicles.filter(v => {
                const term = search.trim().toLowerCase()
                if (!term) return true
                return [
                  v.vin,
                  v.stockNumber,
                  v.year,
                  v.make,
                  v.model,
                  v.trim,
                  v.descriptionStatus,
                  v.highlightStatus,
                ].some(field => String(field ?? '').toLowerCase().includes(term))
              })"
              hover
              item-selectable
              no-data-text="No vehicles found"
              :items-per-page="itemsPerPage"
              :items-per-page-options="[10, 25, 50, 100]"
              :page="currentPage"
              :items-length="totalItems"
              :server-items-length="totalItems"
              must-sort
              item-value="vin"
              class="elevation-0"
              :loading-text="loadingText"
              @click:row="goToDescription"
            >
              <!-- Custom columns for status badges -->
              <!-- A vehicle with no status carries provenance this listing no
                   longer reports, so it shows nothing rather than an empty badge. -->
              <template #item.descriptionStatus="{ item }">
                <v-chip
                  v-if="item.descriptionStatus"
                  :color="getStatusColor(item.descriptionStatus, 'description')"
                  variant="tonal"
                  size="small"
                  class="text-caption"
                >
                  {{ item.descriptionStatus }}
                </v-chip>
              </template>
              
              <template #item.highlightStatus="{ item }">
                <v-chip
                  v-if="item.highlightStatus"
                  :color="getStatusColor(item.highlightStatus, 'highlight')"
                  variant="tonal"
                  size="small"
                  class="text-caption"
                >
                  {{ item.highlightStatus }}
                </v-chip>
              </template>
              
              <!-- Custom pagination info -->
              <template #bottom>
                <div class="d-flex align-center justify-space-between pa-4">
                  <div class="text-caption text-medium-emphasis">
                    Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of {{ totalItems }} vehicles
                  </div>
                  <div class="d-flex align-center">
                    <v-btn
                      icon="mdi-chevron-double-left"
                      variant="text"
                      size="small"
                      :disabled="currentPage === 1"
                      @click="goToPage(1)"
                      class="mr-1"
                    />
                    <v-btn
                      icon="mdi-chevron-left"
                      variant="text"
                      size="small"
                      :disabled="currentPage === 1"
                      @click="goToPage(currentPage - 1)"
                      class="mr-1"
                    />
                    <span class="text-caption mx-2">Page {{ currentPage }} of {{ totalPages }}</span>
                    <v-btn
                      icon="mdi-chevron-right"
                      variant="text"
                      size="small"
                      :disabled="currentPage === totalPages"
                      @click="goToPage(currentPage + 1)"
                      class="mr-1"
                    />
                    <v-btn
                      icon="mdi-chevron-double-right"
                      variant="text"
                      size="small"
                      :disabled="currentPage === totalPages"
                      @click="goToPage(totalPages)"
                    />
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>

        <!-- Vehicle Description View -->
        <v-col
          cols="12"
          v-else-if="currentView === 'list' && vehicleStore.selectedVehicle !== null"
        >
          <div>
            <v-btn
              class="mb-3"
              @click="vehicleStore.clearSelectedVehicle()"
              prepend-icon="mdi-arrow-left"
              text="Back to List"
            />
          </div>
          <vehicleDescriptionTabs />
        </v-col>

        <!-- Prompt Manager View -->
        <v-col cols="12" v-else-if="currentView === 'prompts'">
          <PromptManagerPage :goBack="goBackToList" />
        </v-col>

        <!-- Guardrail Manager View (Admin Only) -->
        <v-col cols="12" v-else-if="currentView === 'guardrails'">
          <GuardrailManagerPage :goBack="goBackToList" />
        </v-col>

        <!-- Account Instructions View -->
        <v-col cols="12" v-else-if="currentView === 'account-instructions'">
          <AccountInstructionsManagerPage :goBack="goBackToList" />
        </v-col>

        <!-- System Instructions View -->
        <v-col cols="12" v-else-if="currentView === 'system'">
          <SystemInstructionsPage :goBack="goBackToList" />
        </v-col>

      </v-row>
    </div>
  </JazelAppContainer>

  <!-- Auto-generate toggle/mode snackbars -->
  <v-snackbar v-model="showAutoGenSnack" color="success" timeout="3000" location="bottom right">
    {{ autoGenSnackText }}
  </v-snackbar>
  <v-snackbar v-model="showAutoGenError" color="error" timeout="4000" location="bottom right">
    {{ autoGenErrorText }}
  </v-snackbar>

  <!-- Sync Descriptions dialog -->
  <v-dialog v-model="showRefreshVehiclesDialog" max-width="520px">
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-sync</v-icon>
        Sync Descriptions
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <div class="text-body-2 text-medium-emphasis mb-3">
          Updates this tool with the latest inventory data. Vehicles may be added or removed based on your feed.
        </div>
        <v-alert type="info" variant="tonal" density="comfortable" class="mb-1">
          <div class="text-caption">This may take a few minutes. A progress screen will appear and other actions will be unavailable until syncing completes.</div>
        </v-alert>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey-darken-1" @click="() => { showRefreshVehiclesDialog = false; advancedMenu = false }">Cancel</v-btn>
        <v-btn color="primary" @click="refreshVehiclesList">Sync Descriptions</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Auto prompt when list is empty -->
  <v-dialog v-model="showAutoPromptDialog" max-width="520px">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-sync</v-icon>
        No descriptions found
      </v-card-title>
      <v-card-text>
        Your list is empty. Would you like to sync descriptions now?
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" color="grey-darken-1" @click="showAutoPromptDialog = false">
          Not now
        </v-btn>
        <v-btn color="primary" @click="handleConfirmAutoSync">Sync Descriptions</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Generate Content dialog -->
  <v-dialog v-model="showGenerateContentDialog" max-width="520px">
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-star-circle-outline</v-icon>
        Generate Content
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <div class="text-body-2 text-medium-emphasis">
          Runs AI content generation for your inventory. This runs in the background—you can keep working.
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey-darken-1" @click="showGenerateContentDialog = false">Cancel</v-btn>
        <v-btn color="primary" @click="handleGenerateContent">Start</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useAccountSettingsStore } from './stores/accountSettings'
import { useDescriptionSyncJobStore } from './stores/descriptionSyncJob'
import { useVehicleStore } from './stores/vehicle'
import { getCdnUrl, getUserFriendlyDescriptionSource, getUserFriendlyHighlightSource, getStatusColor, getThemeColorVar } from './utils'
import { fetchVehicles, getBaselinePrompt, putBaselinePrompt } from './apis'
import { descriptionSyncProgressMessage, isDescriptionSyncTerminal } from './descriptionSyncJob'
import VehicleDescriptionTabs from './components/VehicleDescriptionTabs.vue'
import PromptManagerPage from './components/PromptManagerPage.vue'
import GuardrailManagerPage from './components/GuardrailManagerPage.vue'
const BaselinePromptManagerPage = defineAsyncComponent(() => import('./components/BaselinePromptManagerPage.vue'))
const SystemInstructionsPage = defineAsyncComponent(() => import('./components/SystemInstructionsPage.vue'))
const AccountInstructionsManagerPage = defineAsyncComponent(() => import('./components/AccountInstructionsManagerPage.vue'))
import type { IVehicleTableData } from '@/types'
import { JazelAppContainer } from '@jazel/jazel-vuetify'
import { generateContent as generateContent } from './apis'
import { normalizeProductSettings } from './productSettings'
import { canManageContentLanguage } from './roleCapabilities'
import { useProductSettings } from './useProductSettings'
import ContentLanguageSettings from './components/ContentLanguageSettings.vue'
import ApplyContentLanguage from './components/ApplyContentLanguage.vue'
import LanguageJobProgress from './components/LanguageJobProgress.vue'

const accountSettings = useAccountSettingsStore()
const descriptionSyncJob = useDescriptionSyncJobStore()
const vehicleStore = useVehicleStore()
const { save: saveProductSettings } = useProductSettings()

const currentView = ref<'list' | 'description' | 'prompts' | 'guardrails' | 'baseline' | 'system' | 'account-instructions'>('list')
const showBaselineDialog = ref(false)
const baselineTab = ref<'description' | 'highlight'>('description')
const baselineDescription = ref('')
const baselineHighlight = ref('')
const isSavingBaseline = ref(false)
const activeTab = ref('inventory')

// Role-based access control system (strict role matching)
// Owner access: company_owner, company_suadmin
// Admin access: company_admin, company_owner, company_suadmin, client_admin
const currentRole = computed(() => String(accountSettings.role || '').toLowerCase())
const hasContentLanguageCapability = computed(() => canManageContentLanguage(currentRole.value))

const hasOwnerAccess = computed(() => {
  return currentRole.value === 'company_owner' || currentRole.value === 'company_suadmin'
})

const hasAdminAccess = computed(() => {
  return (
    currentRole.value === 'company_admin' ||
    currentRole.value === 'company_owner' ||
    currentRole.value === 'company_suadmin' ||
    currentRole.value === 'client_admin'
  )
})

const isFetchingVehicleData = ref(false)
const isLoadingListing = ref(false)
const showRefreshVehiclesDialog = ref(false)
const advancedMenu = ref(false)
const showAutoPromptDialog = ref(false)
  const showAutoGenInfoDialog = ref(false)
const hasAutoPromptedSync = ref(false)
const showGenerateContentDialog = ref(false)
// Auto-gen settings UI state - separate toggles for highlights and descriptions
const autoGenerateHighlights = ref<boolean>(
  Boolean((accountSettings as any)?.productSettings?.autoGenerateHighlights ?? false),
)
const autoGenerateDescriptions = ref<boolean>(
  Boolean((accountSettings as any)?.productSettings?.autoGenerateDescriptions ?? true), // Default to true for backward compatibility
)
const autoGenMode = ref<'fill-missing'|'always-ai'>('fill-missing')
const showAutoGenSnack = ref(false)
const autoGenSnackText = ref('Saved')
const showAutoGenError = ref(false)
const autoGenErrorText = ref('Failed to save setting')

/** Reported by the settings controls, which do not own the notification surface. */
const onSettingSaved = (message: string) => {
  autoGenSnackText.value = message
  showAutoGenSnack.value = true
}

const onSettingError = (message: string) => {
  autoGenErrorText.value = message
  showAutoGenError.value = true
}

watch(
  () => (accountSettings as any).productSettings,
  ps => {
    // Read through the shared normalizer: JAMS returns settings wrapped in
    // `config`, so reading keys straight off the stored value misses them.
    const settings = normalizeProductSettings(ps)
    autoGenerateHighlights.value = Boolean(settings.autoGenerateHighlights ?? false)
    autoGenerateDescriptions.value = Boolean(settings.autoGenerateDescriptions ?? true) // Default to true for backward compatibility
    // Initialize Auto-generate settings from product config on load/refresh
    const mode = String(settings.autoGenMode || '').toLowerCase()
    autoGenMode.value = mode === 'always-ai' ? 'always-ai' : 'fill-missing'
  },
  { immediate: true, deep: true },
)

/**
 * The auto-generation keys, which JAMS stores as a set: `autoGenPreferFeed` is
 * derived from the mode, so all four are written together whichever control
 * moved. Only the changed key is passed in; the rest come from current UI state.
 */
const autoGenPatch = (
  changed: Partial<{
    autoGenerateHighlights: boolean
    autoGenerateDescriptions: boolean
    autoGenMode: 'fill-missing' | 'always-ai'
  }>,
) => {
  const mode = changed.autoGenMode ?? autoGenMode.value
  return {
    autoGenerateHighlights: changed.autoGenerateHighlights ?? autoGenerateHighlights.value,
    autoGenerateDescriptions: changed.autoGenerateDescriptions ?? autoGenerateDescriptions.value,
    autoGenMode: mode,
    autoGenPreferFeed: mode === 'fill-missing',
  }
}

/** Save a settings change and report it, restoring the control if it failed. */
const saveSetting = async (
  patch: Record<string, unknown>,
  { success, failure, revert }: { success: string; failure: string; revert: () => void },
) => {
  try {
    if (!(await saveProductSettings(patch))) throw new Error(failure)

    autoGenSnackText.value = success
    showAutoGenSnack.value = true
  } catch (e) {
    console.error(e)
    revert()
    autoGenErrorText.value = failure
    showAutoGenError.value = true
  }
}

const onToggleAutoGenerateHighlights = async (nextValue: boolean | null) => {
  const prev = autoGenerateHighlights.value
  const value = Boolean(nextValue)
  autoGenerateHighlights.value = value

  await saveSetting(autoGenPatch({ autoGenerateHighlights: value }), {
    success: `Saved: Auto-generate Highlights is now ${value ? 'ON' : 'OFF'}`,
    failure: 'Failed to save highlight setting',
    revert: () => (autoGenerateHighlights.value = prev),
  })
}

const onToggleAutoGenerateDescriptions = async (nextValue: boolean | null) => {
  const prev = autoGenerateDescriptions.value
  const value = Boolean(nextValue)
  autoGenerateDescriptions.value = value

  await saveSetting(autoGenPatch({ autoGenerateDescriptions: value }), {
    success: `Saved: Auto-generate Descriptions is now ${value ? 'ON' : 'OFF'}`,
    failure: 'Failed to save description setting',
    revert: () => (autoGenerateDescriptions.value = prev),
  })
}

const onChangeAutoGenMode = async (value: 'fill-missing' | 'always-ai') => {
  const prev = autoGenMode.value
  autoGenMode.value = value

  await saveSetting(autoGenPatch({ autoGenMode: value }), {
    success: `Saved: Mode set to ${value === 'always-ai' ? 'Full AI' : 'Fill Missing'}`,
    failure: 'Failed to save mode setting',
    revert: () => (autoGenMode.value = prev),
  })
}

// New state for enhanced filtering and pagination
const selectedDescriptionFilters = ref<string[]>([])
const selectedHighlightFilters = ref<string[]>([])
const currentPage = ref(1)
const itemsPerPage = ref(25)
const totalItems = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return filteredVehicles.value.length
  return filteredVehicles.value.filter(v => {
    return [
      v.vin,
      v.stockNumber,
      v.year,
      v.make,
      v.model,
      v.trim,
      v.descriptionStatus,
      v.highlightStatus,
    ].some(field => String(field ?? '').toLowerCase().includes(term))
  }).length
})

// Computed properties for filtering and pagination
const descriptionFilters = computed(() => {
  const statuses = [...new Set(vehiclesTableData.value.map(v => v.descriptionStatus))].filter(Boolean)
  return statuses.map(status => ({
    label: status,
    value: status.toLowerCase().replace(/\s+/g, '-')
  }))
})

const highlightFilters = computed(() => {
  const statuses = [...new Set(vehiclesTableData.value.map(v => v.highlightStatus))].filter(Boolean)
  return statuses.map(status => ({
    label: status,
    value: status.toLowerCase().replace(/\s+/g, '-')
  }))
})

const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))

const paginationInfo = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value + 1
  const end = Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
  return { start, end }
})

const loadingText = computed(() => isLoadingListing.value ? 'Loading vehicles...' : '')

// Summary text shown above table – always visible
const filterSummaryText = computed(() => {
  const total = vehiclesTableData.value.length
  const hasChipFilters = selectedDescriptionFilters.value.length > 0 || selectedHighlightFilters.value.length > 0
  const term = search.value.trim().toLowerCase()
  const matches = term
    ? filteredVehicles.value.filter(v => {
        return [
          v.vin,
          v.stockNumber,
          v.year,
          v.make,
          v.model,
          v.trim,
          v.descriptionStatus,
          v.highlightStatus,
        ].some(field => String(field ?? '').toLowerCase().includes(term))
      })
    : filteredVehicles.value

  if (hasChipFilters || !!term) {
    return `${matches.length} of ${total} vehicles match filters${term ? '/search' : ''}`
  }
  return `${total} vehicles found`
})

// Filtered data (OR logic across groups)
const filteredVehicles = computed(() => {
  const all = vehiclesTableData.value
  const hasDesc = selectedDescriptionFilters.value.length > 0
  const hasHigh = selectedHighlightFilters.value.length > 0

  // No filters selected → return all
  if (!hasDesc && !hasHigh) return all

  return all.filter(vehicle => {
    let matchesDesc = false
    let matchesHigh = false

    if (hasDesc) {
      matchesDesc = selectedDescriptionFilters.value.some(filterValue => {
        const filter = descriptionFilters.value.find(f => f.value === filterValue)
        return !!filter && vehicle.descriptionStatus === filter.label
      })
    }

    if (hasHigh) {
      matchesHigh = selectedHighlightFilters.value.some(filterValue => {
        const filter = highlightFilters.value.find(f => f.value === filterValue)
        return !!filter && vehicle.highlightStatus === filter.label
      })
    }

    return hasDesc && hasHigh ? (matchesDesc || matchesHigh) : (hasDesc ? matchesDesc : matchesHigh)
  })
})

// Methods for pagination
const goToPage = (page: number) => {
  currentPage.value = page
}

// Mobile detection
const detectMobile = () => {
  isMobile.value = window.innerWidth < 768
}

const refreshingVehicles = computed(
  () => descriptionSyncJob.starting || descriptionSyncJob.hasJob,
)
const progress = computed(() => descriptionSyncJob.job?.percentage ?? 0)
const progressMessage = computed(() =>
  descriptionSyncJob.refreshFailure
    ? 'Could not refresh sync progress. Retrying…'
    : descriptionSyncProgressMessage(descriptionSyncJob.job),
)
const syncProgressColor = computed(() => {
  const status = descriptionSyncJob.job?.status
  if (status === 'failed') return 'error'
  if (status === 'completed-with-issues') return 'warning'
  if (status === 'completed') return 'success'
  return 'primary'
})

const vehiclesTableData = ref<IVehicleTableData[]>([])
const search = ref('')

// Mobile experience
const isMobile = ref(false)

const tableHeaders = [
  { title: 'VIN', key: 'vin', class: 'font-weight-bold' },
  { title: 'STOCK #', key: 'stockNumber', class: 'font-weight-bold' },
  { title: 'YEAR', key: 'year', class: 'font-weight-bold' },
  { title: 'MAKE', key: 'make', class: 'font-weight-bold' },
  { title: 'MODEL', key: 'model', class: 'font-weight-bold' },
  { title: 'TRIM', key: 'trim', class: 'font-weight-bold' },
  { title: 'DESCRIPTION', key: 'descriptionStatus', class: 'font-weight-bold' },  
  { title: 'HIGHLIGHT', key: 'highlightStatus', class: 'font-weight-bold' },  
]

const refreshVehiclesList = async () => {
  showRefreshVehiclesDialog.value = false
  advancedMenu.value = false

  const started = await descriptionSyncJob.start()
  if (!started && descriptionSyncJob.startFailure) {
    autoGenErrorText.value = 'Could not start description sync. Try again.'
    showAutoGenError.value = true
  }
}

const goToDescription = (_: any, item: any) => {
  vehicleStore.setSelectedVehicle(item.item)
}

const populateVehicles = (vehicles: any) => {
  vehiclesTableData.value = Object.entries(vehicles).map(([key, value]: [string, any]) => {
    return {
      vin: key,
      stockNumber:
        value.stockNumber === 'undefined' || value.stockNumber === null ? '' : value.stockNumber,
      year: value.year,
      make: value.make,
      model: value.model,
      trim: value.trim,
      descriptionStatus: getUserFriendlyDescriptionSource(value.source),
      highlightStatus: getUserFriendlyHighlightSource(value.highlightSource),
        descriptionLocked: !!value.descriptionLocked,
    }
  })
}

const updateVehicleListing = async (accountId: string) => {
  isFetchingVehicleData.value = true
  vehiclesTableData.value = []

  try {
    const vehicleData: any[] = await fetchVehicles(accountId)

    if (vehicleData.length === 0) {
      console.error('No vehicles found for this account.')
    }

    if (accountSettings.accountId === accountId) populateVehicles(vehicleData)
  } catch (error: any) {
    console.error('Error fetching vehicles:', error)
    throw new Error(error.message)
  } finally {
    isFetchingVehicleData.value = false
  }
}

const openBaselinePromptDialog = async () => {
  showBaselineDialog.value = true
  baselineTab.value = 'description'
  try {
    const [desc, high] = await Promise.all([
      getBaselinePrompt(accountSettings.accountId, 'description'),
      getBaselinePrompt(accountSettings.accountId, 'highlight'),
    ])
    baselineDescription.value = desc || ''
    baselineHighlight.value = high || ''
  } catch (e) {
    baselineDescription.value = ''
    baselineHighlight.value = ''
  }
}

const saveBaselinePrompts = async () => {
  isSavingBaseline.value = true
  try {
    await Promise.all([
      putBaselinePrompt(accountSettings.accountId, 'description', baselineDescription.value || ''),
      putBaselinePrompt(accountSettings.accountId, 'highlight', baselineHighlight.value || ''),
    ])
    showBaselineDialog.value = false
  } finally {
    isSavingBaseline.value = false
  }
}

const copyStatus = ref('')

const navigateToPromptManager = () => {
  currentView.value = 'prompts'
}

const navigateToGuardrailManager = () => {
  currentView.value = 'guardrails'
}

const navigateToBaselineManager = () => {
  currentView.value = 'baseline'
}

const navigateToSystemInstructions = () => {
  currentView.value = 'system'
}

const navigateToAccountInstructions = () => {
  currentView.value = 'account-instructions'
}

const goBackToList = () => {
  currentView.value = 'list'
  vehicleStore.clearSelectedVehicle()
}

const copyToClipboard = async () => {
  try {
    const cdn = getCdnUrl()
    const content = `<script src='${cdn}/aie-desc-gen/cx/loader.js'><\/script><div class='aie-descriptions-cx' data-account-id='${accountSettings.accountId}' data-vin='' data-display-contents='highlight,description'></div>`
    await navigator.clipboard.writeText(content)
    copyStatus.value = 'Copied to clipboard!'

    // Reset the icon and status after 2 seconds
    setTimeout(() => {
      copyStatus.value = ''
      advancedMenu.value = false
    }, 1500)
  } catch (err) {
    copyStatus.value = 'Failed to copy text'
    console.error('Failed to copy text:', err)
  }
}

watch(
  () => accountSettings.accountId,
  async (accountId: any) => {
    if (accountId === '') {
      return
    }

    try {
      isLoadingListing.value = true
      await updateVehicleListing(accountSettings.accountId)
      if (!hasAutoPromptedSync.value && vehiclesTableData.value.length === 0) {
        showAutoPromptDialog.value = true
        hasAutoPromptedSync.value = true
      }
    } catch (error: any) {
      console.error('Error fetching essential data:', error)
    } finally {
      isLoadingListing.value = false
    }
  },
)

let terminalDismissTimer: ReturnType<typeof setTimeout> | null = null
let refreshedTerminalJobId = ''

watch(
  () => descriptionSyncJob.job,
  async currentJob => {
    if (
      !currentJob ||
      !isDescriptionSyncTerminal(currentJob.status) ||
      currentJob.jobId === refreshedTerminalJobId
    ) {
      return
    }

    refreshedTerminalJobId = currentJob.jobId
    if (accountSettings.accountId === currentJob.accountId) {
      try {
        isLoadingListing.value = true
        await updateVehicleListing(currentJob.accountId)
      } catch (error) {
        console.error('Error refreshing inventory after description sync:', error)
      } finally {
        isLoadingListing.value = false
      }
    }

    if (descriptionSyncJob.jobId !== currentJob.jobId) return
    terminalDismissTimer = setTimeout(() => {
      if (descriptionSyncJob.jobId === currentJob.jobId) descriptionSyncJob.reset()
    }, 1200)
  },
  { deep: true },
)

watch(
  () => vehicleStore.selectedVehicle,
  async (selectedVehicle) => {
    if (selectedVehicle == null) {
      currentView.value = 'list'
      isLoadingListing.value = true
      await updateVehicleListing(accountSettings.accountId)
      isLoadingListing.value = false
      if (!hasAutoPromptedSync.value && vehiclesTableData.value.length === 0) {
        showAutoPromptDialog.value = true
        hasAutoPromptedSync.value = true
      }
    } else {
      currentView.value = 'list' // Stay in list view but show vehicle description
    }
  },
)

// Watch for filter changes to reset pagination
watch([selectedDescriptionFilters, selectedHighlightFilters, () => filteredVehicles.value.length], () => {
  // Reset or clamp page when filters/data change
  const pages = totalPages.value
  currentPage.value = Math.min(currentPage.value, pages) || 1
})

// Watch for data changes to reset filters
watch(vehiclesTableData, () => {
  selectedDescriptionFilters.value = []
  selectedHighlightFilters.value = []
  currentPage.value = 1
}, { deep: true })

// Watch for role changes to handle tiered access restrictions
watch(
  () => accountSettings.role,
  newRole => {
    // If user loses access while on restricted views, redirect to list view
    if (currentView.value === 'guardrails' && !hasOwnerAccess.value) {
      // Owner lost access to guardrails
      currentView.value = 'list'
    } else if (currentView.value === 'prompts' && !hasAdminAccess.value) {
      // Admin lost access to prompts
      currentView.value = 'list'
    }
  },
)

const handleConfirmAutoSync = () => {
  showAutoPromptDialog.value = false
  showRefreshVehiclesDialog.value = true
}

const handleGenerateContent = async () => {
  try {
    await generateContent({ accountId: accountSettings.accountId })
    showGenerateContentDialog.value = false
  } catch (e) {
    console.error('Failed to start content generation', e)
  }
}

const clearAllFilters = () => {
  selectedDescriptionFilters.value = []
  selectedHighlightFilters.value = []
}

onMounted(() => {
  detectMobile()
  window.addEventListener('resize', detectMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', detectMobile)
  if (terminalDismissTimer !== null) clearTimeout(terminalDismissTimer)
})

// keep tab reflective of current list/description view
watch(currentView, (view) => {
  if (view === 'list' || view === 'description') {
    activeTab.value = 'inventory'
  }
})
</script>

<style scoped>
.single-line-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
  width: 150px;
}

:deep(.icon-hover-black:hover:before) {
  color: #444;
  transition: all 0.3s ease;
}

:deep(.fade-enter-active),
:deep(.fade-leave-active) {
  transition: opacity 0.8s ease;
}

:deep(.fade-enter-from),
:deep(.fade-leave-to) {
  opacity: 0;
}
.align-right {
  text-align: right;
}

/* Filter and action styling */
.v-chip-group {
  flex-wrap: wrap;
}

.gap-2 > * {
  margin: 4px;
}

/* Enhanced filter styling */
.filter-summary {
  background: rgba(25, 118, 210, 0.04);
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
}

/* Status badge enhancements */
:deep(.v-chip--variant-tonal) {
  font-weight: 500;
  letter-spacing: 0.025em;
}

:deep(.v-chip--size-small) {
  min-height: 24px;
  padding: 0 8px;
}

/* Table improvements */
:deep(.v-data-table) {
  border-radius: 8px;
}

:deep(.v-data-table-header) {
  background-color: #f8f9fa;
}

:deep(.v-data-table-header th) {
  font-weight: 700 !important;
  color: #2c3e50;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 8px;
}

:deep(.v-data-table thead th) {
  font-weight: 700 !important;
  color: #2c3e50;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 8px;
}

:deep(.v-data-table-header .v-data-table__th) {
  font-weight: 700 !important;
  color: #2c3e50;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 8px;
}

/* Aggressive header styling to ensure bold text */
:deep(.v-data-table th),
:deep(.v-data-table-header th),
:deep(.v-data-table thead th),
:deep(.v-data-table__th) {
  font-weight: 700 !important;
  color: #2c3e50 !important;
  font-size: 0.875rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

:deep(.v-data-table__wrapper) {
  border-radius: 8px;
}

/* Pagination improvements */
:deep(.v-data-table-footer) {
  border-top: 1px solid #e9ecef;
}

/* Filter section styling */
.filter-section {
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 16px;
}

/* Quick actions styling */
.quick-actions {
  background: rgba(25, 118, 210, 0.04);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

/* Info icon subtle opacity */
.section-info-btn {
  opacity: 0.7;
}
.section-info-btn:hover {
  opacity: 1;
}

/* Menu item hover affordance */
.v-list-item:not(.v-list-subheader):hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

/* Mobile-specific styles */
@media (max-width: 767px) {
  .v-data-table {
    font-size: 0.875rem;
  }
  
  .v-data-table th,
  .v-data-table td {
    padding: 8px 4px;
  }
  
  .v-chip {
    margin-bottom: 4px;
  }
  
  .v-btn {
    min-height: 44px; /* Touch-friendly minimum height */
  }
  
  .v-text-field {
    min-height: 48px; /* Touch-friendly input height */
  }
}

/* Pill-style toggle switch */
.pill-toggle {
  background: rgba(0, 0, 0, 0.06) !important; /* container backdrop for separation */
  padding: 4px !important;
}
.pill-toggle :deep(.v-btn) {
  box-shadow: none !important;
  border: none !important;
  background: transparent !important; /* inactive = transparent */
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.pill-toggle :deep(.v-btn .v-btn__content) {
  color: #aaa !important; /* high-contrast gray */
  font-weight: 500;
}

/* No Auto-generate - Gray/inactive */
.pill-toggle :deep(.v-btn[value="false"].v-btn--active) {
  background: rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}
.pill-toggle :deep(.v-btn[value="false"].v-btn--active .v-btn__content) {
  color: rgba(0, 0, 0, 0.6) !important;
  font-weight: 500;
}

/* Auto-generate - Green/active */
.pill-toggle :deep(.v-btn[value="true"].v-btn--active) {
  background: #10B981 !important; /* emerald green for "on" */
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3) !important;
}
.pill-toggle :deep(.v-btn[value="true"].v-btn--active .v-btn__content) {
  color: #ffffff !important;
  font-weight: 600;
}

/* Increase internal padding for readability */
.pill-toggle :deep(.v-btn .v-btn__content) {
  padding-inline: 10px;
}

/* Normalize toggle heights on larger screens for consistent centering */
.pill-toggle :deep(.v-btn) {
  height: 28px;
  min-height: 28px;
}

/* Mobile alignment fix for pill toggles */
@media (max-width: 600px) {
  .pill-toggle {
    display: inline-flex;
    align-items: center;
    padding: 2px !important; /* slightly tighter on mobile */
    border-radius: 999px !important;
  }
  .pill-toggle :deep(.v-btn) {
    height: 30px;
    min-height: 30px;
    border-radius: 999px !important;
    padding: 0 !important; /* remove extra vertical padding */
  }
  .pill-toggle :deep(.v-btn .v-btn__content) {
    line-height: 1; /* true vertical centering */
    padding-inline: 12px; /* consistent horizontal spacing */
    font-size: 12px;
  }
}
</style>
